import * as React from 'react';
import { Res, BuyerCustomerDTO, CustomerDetailsDTO, Permission } from 'core/api';
import { ActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import * as Images from 'pos/Images';
import { Command } from 'pos/common';
import { Preconditions } from 'core/infrastructure-next';
import { CustomerFacade } from 'pos/facades';
import selectParentCustomer from './selectParentCustomer';
import { Message, Modal } from 'core/ui-next/modal';
import { BarcodeReader, SvgIcon } from 'core/ui-next';
import { Box, HStack, VStack, Text, SegmentGroup } from '@chakra-ui/react';
import { administrativePanel, commercialTermsPanel, contactPanel, locationPanel, notesPanel, promotionsPanel, promotionsPhonePanel } from './showCustomerDetailsPannels';
import { isPhone } from 'pos/ui/ThemeManager';

const EMPTY_CUSTOMER: CustomerDetailsDTO = {
	Address: { City: '', Region: '', Street: '', ZipCode: '' },
	ApplySurcharge: false,
	BusinessName: '',
	ContactPerson: '',
	Email: '',
	SendMailing: false,
	Offers: [],
	Cif: '',
	DiscountRate: 0,
	FiscalName: '',
	Id: 0,
	AccountCode: '',
	CardNumber: '',
	Notes: '',
	PriceList: '',
	ShowNotes: false,
	Type: { Id: 0, Name: '' },
	ParentCustomerId: 0,
	Telephone: '',
	CountryCode: '',
};

type Params = {
	customer: BuyerCustomerDTO;
	setCustomer: (customer: BuyerCustomerDTO) => Promise<void>;
	hideRemoveCustomerButton?: boolean;
};

type Props = {
	customer: CustomerDetailsDTO,
	setCustomer: (customer: BuyerCustomerDTO) => Promise<void>;
	hideRemoveCustomerButton?: boolean;
} & Modal.Actions<BuyerCustomerDTO>;

function ShowCustomerScreen(props: Props) {
	const { customer: initialCustomer, setCustomer, hideRemoveCustomerButton, cancel, close } = props;

	const [customer, setLocalCustomer] = React.useState<CustomerDetailsDTO>(initialCustomer);
	const [expanded, setExpanded] = React.useState(false);


	React.useEffect(() => {
		setLocalCustomer(initialCustomer);
	}, [initialCustomer]);


	const closeWithCustomer = React.useCallback(async (customer: CustomerDetailsDTO) => {
		close(customer);
		await setCustomer(customer);
	}, [setCustomer, close]);


	const onCardNumberRead = React.useCallback(async (cardNumber: string) => {
		let customer = await CustomerFacade.getDetailsByCardNumber(cardNumber);

		if (customer.Id === 0) {
			await Message.show(Res.Msg.MSG_CUSTOMER_CARD_NOT_FOUND());
			return;
		}

		if (customer.ParentCustomerId > 0) {
			const parentCustomer = await CustomerFacade.getDetails(customer.ParentCustomerId);
			const result = await selectParentCustomer(customer, parentCustomer);

			if (!result.ok)
				return;

			customer = result.value;
		}

		await closeWithCustomer(customer);
	}, [closeWithCustomer]);


	React.useEffect(() => {
		let disposeReader: () => void = () => { };

		disposeReader = BarcodeReader.start(onCardNumberRead);

		return () => {
			disposeReader();
		};
	}, [onCardNumberRead]);


	const onChangeCustomer = React.useCallback(async () => {
		const result = await Command.execute('searchCustomerByText');
		if (result.ok) {
			// No hace falta convertir a BuyerCustomerDTO, porque la estructura es igual
			await closeWithCustomer(result.value);
		}
	}, [closeWithCustomer]);


	const onEditCustomer = React.useCallback(async () => {
		const result = await Command.execute('editCustomer', customer!.Id);
		if (!result.ok)
			return;

		const refreshedCustomer = await CustomerFacade.getDetails(customer!.Id);
		setLocalCustomer(refreshedCustomer);
		await setCustomer(refreshedCustomer);
	}, [customer, setCustomer]);


	const onRemoveCustomer = React.useCallback(async () => {
		await closeWithCustomer(EMPTY_CUSTOMER);
	}, [closeWithCustomer]);


	const onShowLastSales = React.useCallback(async () => {
		await Command.execute('showRecentSalesByCustomer', customer);
	}, [customer]);


	const onCancel = React.useCallback(async () => cancel(), [cancel]);


	// Lista con los identificadores de cada sección (pestaña) que se muestra en la ficha del cliente.
	// Se usa useMemo para que el array se cree una sola vez y no cambie en cada render.
	// El "as const" permite que TypeScript infiera los valores exactos como tipo literal.
	// Tipo SegmentKey que representa cualquiera de los identificadores anteriores ('adm' | 'contact' | ...).
	const SEGMENT_KEYS = React.useMemo(() => ['adm', 'contact', 'location', 'terms', 'offers', 'notes'] as const, []);
	type SegmentKey = typeof SEGMENT_KEYS[number];

	// Pares clave/etiqueta que se pintan como pestañas en la barra superior.
	// La clave identifica la sección y la etiqueta es el texto traducido que ve el usuario.
	const segmentItems: { key: SegmentKey, label: string }[] = [
		{ key: 'adm', label: Res.Web.SHOW_CUSTOMER_ADMINISTRATIVE_DATA_LBL() },
		{ key: 'contact', label: Res.Web.SHOW_CUSTOMER_CONTACT_LBL() },
		{ key: 'location', label: Res.Web.SHOW_CUSTOMER_LOCATION_LBL() },
		{ key: 'terms', label: Res.Web.SHOW_CUSTOMER_COMMERCIAL_TERMS_LBL() },
		{ key: 'offers', label: Res.Web.SHOW_CUSTOMER_OFFERS_LBL() },
		{ key: 'notes', label: Res.Web.SHOW_CUSTOMER_NOTES_LBL() }
	];

	// Referencia al contenedor con scroll donde se muestran los paneles de cada sección.
	const panelScrollRef = React.useRef<HTMLDivElement>(null);
	// Referencias a cada panel (uno por sección) para poder hacer scroll hasta ellos.
	const panelRefs = React.useRef<Record<SegmentKey, HTMLDivElement | null>>({
		adm: null, contact: null, location: null, terms: null, offers: null, notes: null
	});
	// Referencias a cada pestaña de la barra superior para poder centrarlas al cambiar de sección.
	const segmentRefs = React.useRef<Record<SegmentKey, HTMLElement | null>>({
		adm: null, contact: null, location: null, terms: null, offers: null, notes: null
	});

	// Guarda la sección activa en una ref para poder consultarla desde callbacks sin provocar renders y en un estado con la pestaña activa.
	const activeSegmentRef = React.useRef<SegmentKey>('adm');
	const [activeSegment, setActiveSegment] = React.useState<SegmentKey>('adm');


	// Hace scroll horizontal en la barra de pestañas para dejar centrada la pestaña indicada.
	const centerSegment = React.useCallback((key: SegmentKey) => {
		segmentRefs.current[key]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
	}, []);


	// Se dispara al pulsar una pestaña: marca la sección como activa, desplaza el panel correspondiente
	// a la parte superior del contenedor y centra la pestaña en la barra.
	const onScrollIntoPanel = (key: SegmentKey, segmentEl: HTMLElement | null) => {
		activeSegmentRef.current = key;
		setActiveSegment(key);

		panelRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		segmentEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
	};


	// Esto es un efecto que sincroniza la pestaña activa con el scroll del usuario dentro de los paneles.
	// Usa IntersectionObserver para detectar qué panel está visible en el centro del contenedor
	// (rootMargin -45%/-45% define esa "franja central") y actualizar la pestaña resaltada.
	React.useEffect(() => {
		const container = panelScrollRef.current;
		if (!container)
			return;

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				// Solo nos interesan los paneles que estén entrando en la franja visible.
				if (!entry.isIntersecting)
					return;

				// Recuperamos la clave de sección desde el atributo data-segment-key del panel.
				const key = entry.target.getAttribute('data-segment-key') as SegmentKey | null;
				// Si no hay clave o ya es la sección activa, no hacemos nada para evitar renders innecesarios.
				if (!key || activeSegmentRef.current === key)
					return;

				activeSegmentRef.current = key;
				setActiveSegment(key);
				centerSegment(key);
			});
		}, { root: container, rootMargin: '-45% 0px -45% 0px', threshold: 0 });

		// Registramos cada panel para que el observer vigile su visibilidad.
		SEGMENT_KEYS.forEach(key => {
			const el = panelRefs.current[key];
			if (el)
				observer.observe(el);
		});

		// Al desmontar el componente o cambiar de cliente, dejamos de observar para liberar recursos.
		return () => observer.disconnect();

	}, [customer.Id, centerSegment, SEGMENT_KEYS]);


	return (
		<PosScreen size={{ width: 1000, height: 720 }} screenId='SHOW_CUSTOMER_DETAILS' useKeyPad={false}>
			<Header title={Res.Web.SHOW_CUSTOMER_TITLE()} onClose={cancel} />
			<Content>
				<VStack width={'full'} height={'full'} paddingX={isPhone() ? '2' : '6'} paddingY={isPhone() ? '4' : '6'} bg={{ base: 'neutral.50', _dark: 'neutral.900' }}>
					<VStack width={'full'} gap={'6'} alignItems={'stretch'} minH={'0'}>
						<HStack width={'full'}>
							<HStack width={'full'} gap={'2'} justifyContent={'start'} alignItems={'center'}>
								<SvgIcon src={Images.Profile} width={isPhone() ? '5' : '7'} height={isPhone() ? '5' : '7'} />
								<Text fontSize={isPhone() ? 'lg' : 'xl'} fontWeight={'semibold'}>{customer.FiscalName}</Text>
							</HStack>
							<LargeDeviceOnly>
								<HStack width={'64'} gap={'0.5'} justifyContent={'start'} alignItems={'self-start'}>
									{!hideRemoveCustomerButton && customer.Id > 0 &&
										<VStack width={'15'} gapY={'1'} gapX={'1.5'} cursor={'pointer'} onClick={onRemoveCustomer}>
											<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
												<SvgIcon src={Images.DeleteUser} width={'6'} height={'6'} />
											</Box>
											<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{Res.Web.SHOW_CUSTOMER_DELETE_SELECTED_BTN()}</Text>
										</VStack>
									}
									<VStack width={'15'} gapY={'1'} gapX={'1.5'} cursor={'pointer'} onClick={onShowLastSales}>
										<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
											<SvgIcon src={Images.RecentSales} width={'6'} height={'6'} />
										</Box>
										<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{Res.Web.SHOW_CUSTOMER_LAST_SALES_BTN()}</Text>
									</VStack>
									<VStack width={'15'} gapY={'1'} gapX={'1.5'} cursor={'pointer'} onClick={onEditCustomer}>
										<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
											<SvgIcon src={Images.EditUser} width={'6'} height={'6'} />
										</Box>
										<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{Res.Web.SHOW_CUSTOMER_EDIT_BTN()}</Text>
									</VStack>
									<VStack width={'15'} gapY={'1'} gapX={'1.5'} cursor={'pointer'} onClick={onChangeCustomer}>
										<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
											<SvgIcon src={Images.ChangeCustomer} width={'6'} height={'6'} />
										</Box>
										<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{Res.Web.SHOW_CUSTOMER_CHANGE_BTN()}</Text>
									</VStack>
								</HStack>
							</LargeDeviceOnly>
						</HStack>
						<LargeDeviceOnly>
							<VStack width={'full'} flex={'1'} minH={'0'} overflowY={'auto'} >
								<HStack width={'full'} gap={'4'} padding={'1'}>
									{administrativePanel(customer)}
									{contactPanel(customer)}
								</HStack>
								<HStack width={'full'} gap={'4'} justifyContent={'end'}>
									<HStack textAlign={'center'} onClick={() => setExpanded(!expanded)} cursor={'pointer'}>
										<Text fontSize={''} fontWeight={''} color={'info'}>{expanded ? Res.Web.SHOW_CUSTOMER_SHOW_LESS_LBL() : Res.Web.SHOW_CUSTOMER_SHOW_MORE_LBL()}</Text>
										<SvgIcon src={expanded ? Images.ChevronUp : Images.ChevronDown} width={'4.5'} height={'4.5'} color={'info'} />
									</HStack>
								</HStack>
								{expanded &&
									<VStack width={'full'} gap={'6'}>
										<HStack width={'full'} gap={'4'} padding={'1'}>
											{locationPanel(customer)}
											{commercialTermsPanel(customer)}
										</HStack>
										<HStack width={'full'} gap={'4'} padding={'1'}>
											{promotionsPanel(customer)}
											{notesPanel(customer)}
										</HStack>
									</VStack>
								}
							</VStack>
						</LargeDeviceOnly>
						<PhoneOnly>
							<HStack width={'full'} minH={'10'} overflowY={'hidden'}>
								<SegmentGroup.Root value={activeSegment} onValueChange={(e) => setActiveSegment(e.value as SegmentKey)} borderRadius={'md'} shadow={'sm'} size={'md'}
									css={{
										'--segment-indicator-bg': 'var(--chakra-colors-primary)',
									}}
								>
									<SegmentGroup.Indicator />
									{segmentItems.map((item, index) => (
										<SegmentGroup.Item
											key={index}
											value={item.key}
											ref={(el: HTMLElement | null) => { segmentRefs.current[item.key as SegmentKey] = el; }}
											onClick={(e) => onScrollIntoPanel(item.key as SegmentKey, e.currentTarget)}
										>
											<SegmentGroup.ItemText _checked={{ color: 'white' }}>{item.label}</SegmentGroup.ItemText>
											<SegmentGroup.ItemHiddenInput />
										</SegmentGroup.Item>
									))}
								</SegmentGroup.Root>
							</HStack>
							<VStack ref={panelScrollRef} width={'full'} gap={'6'} minH={'0'} overflowY={'auto'}>
								<Box width={'full'} data-segment-key={'adm'} ref={(el: HTMLDivElement | null) => { panelRefs.current.adm = el; }}>{administrativePanel(customer)}</Box>
								<Box width={'full'} data-segment-key={'contact'} ref={(el: HTMLDivElement | null) => { panelRefs.current.contact = el; }}>{contactPanel(customer)}</Box>
								<Box width={'full'} data-segment-key={'location'} ref={(el: HTMLDivElement | null) => { panelRefs.current.location = el; }}>{locationPanel(customer)}</Box>
								<Box width={'full'} data-segment-key={'terms'} ref={(el: HTMLDivElement | null) => { panelRefs.current.terms = el; }}>{commercialTermsPanel(customer)}</Box>
								<Box width={'full'} data-segment-key={'offers'} ref={(el: HTMLDivElement | null) => { panelRefs.current.offers = el; }}>{promotionsPhonePanel(customer)}</Box>
								<Box width={'full'} data-segment-key={'notes'} ref={(el: HTMLDivElement | null) => { panelRefs.current.notes = el; }}>{notesPanel(customer)}</Box>
							</VStack>
						</PhoneOnly>
					</VStack>
				</VStack >
			</Content >
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
					<Filler />
					{!hideRemoveCustomerButton && customer.Id > 0 &&
						<ActionButton title={Res.Web.SHOW_CUSTOMER_DELETE_SELECTED_BTN()} image={Images.DeleteUser} action={onRemoveCustomer} />
					}
					<ActionButton title={Res.Web.SHOW_CUSTOMER_LAST_SALES_BTN()} image={Images.RecentSales} action={onShowLastSales} />
					<ActionButton title={Res.Web.SHOW_CUSTOMER_EDIT_BTN()} image={Images.EditUser} action={onEditCustomer} />
					<ActionButton title={Res.Web.SHOW_CUSTOMER_CHANGE_BTN()} image={Images.ChangeCustomer} action={onChangeCustomer} />
				</PhoneOnly>
			</Footer>
		</PosScreen >
	);
}


declare global {
	interface CommandType {
		showCustomerDetails: (params: Params) => Promise<Result<BuyerCustomerDTO>>
	}
}

Command.register('showCustomerDetails', Preconditions.ensure(Permission.Customer_ShowDetails), async (params: Params): Promise<Result<BuyerCustomerDTO>> => {
	const customer = await CustomerFacade.getUpdatedDetails(params.customer);
	const props = { ...params, customer };
	return Modal.show(ShowCustomerScreen, { ...props });
});
